import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DefaultCompany from '../icons/building_round.svg';
import { useBoardOwner } from '../hooks/useBoardOwner';
import { format } from 'date-fns';
import { buildEmbeddableUrl } from '../utils/video';
import TimeIcon from './BoardMeetingsCard/time.png';

const withProtocol = (value) => {
  if (!value) return '';
  return value.startsWith('http') ? value : `https://${value}`;
};

const getCompanyLogo = (company) => {
  const image = company?.image || {};
  if (image.id) {
    return `${process.env.REACT_APP_IMAGES_URL}${image.id}`;
  }
  if (image.imageKey) {
    return `${process.env.REACT_APP_IMAGES_URL}${image.imageKey}`;
  }
  if (image.location) {
    return withProtocol(image.location);
  }
  if (company?.companyLogo) {
    return company.companyLogo;
  }
  return DefaultCompany;
};

const getBoardOwnerAvatar = (contact) => {
  if (!contact) return DefaultCompany;
  if (contact.image?.imageKey) {
    return `${process.env.REACT_APP_IMAGES_URL}${contact.image.imageKey}`;
  }
  if (contact.image?.id) {
    return `${process.env.REACT_APP_IMAGES_URL}${contact.image.id}`;
  }
  if (contact.image?.location) {
    return withProtocol(contact.image.location);
  }
  if (contact.contactLogoLocation) {
    return withProtocol(contact.contactLogoLocation);
  }
  return DefaultCompany;
};

const getPlainTextFromDraft = (body) => {
  if (!body) return '';
  try {
    const parsed = JSON.parse(body);
    if (Array.isArray(parsed.blocks)) {
      return parsed.blocks.map((block) => block.text).join('\n');
    }
  } catch (error) {
    return body;
  }
  return body;
};

export default function BoardUpdateCard({ activity, company }) {
  const boardOwner = useBoardOwner(company?.contacts);
  const postUpdate = activity?.postUpdate;
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  if (!postUpdate) {
    return null;
  }

  const createdAt = postUpdate?.createdAt || activity?.createdAt;

  const formattedDate = useMemo(() => {
    if (!createdAt) return '';
    try {
      return format(new Date(createdAt), 'MMMM d, yyyy');
    } catch (error) {
      return '';
    }
  }, [createdAt]);

  const bodyText = useMemo(
    () => getPlainTextFromDraft(postUpdate.body).trim(),
    [postUpdate.body]
  );

  const videoUrl = useMemo(
    () => buildEmbeddableUrl(postUpdate?.video?.url || postUpdate?.streamLink),
    [postUpdate?.video?.url, postUpdate?.streamLink]
  );

  const companyLogo = useMemo(() => getCompanyLogo(company), [company]);
  const boardOwnerAvatar = useMemo(
    () => getBoardOwnerAvatar(boardOwner),
    [boardOwner]
  );
  const dateChipStyles = useMemo(
    () => ({
      width: isXS ? '100%' : 'auto',
      backgroundColor: isXS ? '#F4F8FF' : 'white',
      borderRadius: 16,
      padding: '0 8px',
      color: '#124CB9',
      border: isXS ? 'none' : '1px solid #B1CBFC',
      fontWeight: 400,
    }),
    [isXS]
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 12px 32px rgba(0, 10, 60, 0.06)',
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            src={companyLogo}
            alt={`${company?.companyName} logo`}
            sx={{ width: 40, height: 40 }}
          />
        }
        title={
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#001030' }}>
            {company?.companyName}
          </Typography>
        }
        // subheader={
        //   <Typography sx={{ fontSize: 14, color: '#4D586F' }}>
        //     Board Update
        //   </Typography>
        // }
        sx={{
          borderBottom: '1px solid #E6EDFF',
          height: '82px',
        }}
      />
      <CardContent sx={{ p: { xxs: 2, sm: 3 } }}>
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 600,
            color: '#001030',
            lineHeight: '130%',
            mb: 1,
          }}
        >
          {company?.companyName} Board Update
        </Typography>
        {formattedDate && (
          <Tooltip title={formattedDate}>
            <Chip
              icon={
                <img
                  src={TimeIcon}
                  alt="time"
                  style={{ width: 20, height: 20 }}
                />
              }
              label={formattedDate}
              sx={{ ...dateChipStyles, mb: 2 }}
            />
          </Tooltip>
        )}
        {bodyText && (
          <Typography
            sx={{
              color: '#4D586F',
              fontSize: 14,
              lineHeight: '150%',
              mb: 2,
              whiteSpace: 'pre-wrap',
            }}
          >
            {bodyText}
          </Typography>
        )}
        {videoUrl && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 540,
              height: 305,
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: '#000',
              mb: 3,
            }}
          >
            <Box
              component="iframe"
              src={videoUrl}
              title="Board update video"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        )}
        {boardOwner && (
          <Box
            sx={{
              borderRadius: '8px',
              backgroundColor: '#F4F8FF',
              minWidth: '300px',
              height: '64px',
              display: 'inline-flex',
              alignItems: 'center',
              p: 1.5,
              mt: 1,
            }}
          >
            <Avatar
              src={boardOwnerAvatar}
              alt={boardOwner.displayName}
              sx={{ width: 40, height: 40, mr: 2 }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: '130%',
                  color: '#001030',
                  mb: '2px',
                }}
              >
                {boardOwner.displayName}
              </Typography>
              {boardOwner.title ? (
                <Typography sx={{ fontSize: 14, color: '#4D586F' }}>
                  {boardOwner.title},{' '}
                  {boardOwner.companyName || company?.companyName}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 14, color: '#4D586F' }}>
                  {boardOwner.companyName || company?.companyName}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

BoardUpdateCard.propTypes = {
  activity: PropTypes.shape({
    postUpdate: PropTypes.shape({
      body: PropTypes.string,
      createdAt: PropTypes.string,
      video: PropTypes.shape({
        url: PropTypes.string,
      }),
      streamLink: PropTypes.string,
    }),
    createdAt: PropTypes.string,
  }).isRequired,
  company: PropTypes.shape({
    companyName: PropTypes.string,
    companyLogo: PropTypes.string,
    contacts: PropTypes.arrayOf(PropTypes.shape({})),
    image: PropTypes.shape({
      id: PropTypes.string,
      imageKey: PropTypes.string,
      location: PropTypes.string,
    }),
  }).isRequired,
};
